import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';

/**
 * Standalone anon-key Supabase client used by the public routes
 * (`/calendar` and `/events/:id`).
 *
 * IMPORTANT: This is intentionally NOT the same instance as
 * `src/providers/supabase/supabase.ts`. The app-shell client carries the
 * authenticated user's session in localStorage and is wired into
 * react-admin's authProvider. Sharing it here would risk:
 *   - elevating the public route to authenticated reads when a user with
 *     an active session opens `/calendar` (Pitfall 5 territory),
 *   - coupling the public bundle to the authProvider chain.
 *
 * `persistSession: false` + `autoRefreshToken: false` keep this client
 * permanently anon. It hits the same Postgres database, but RLS scopes
 * the result naturally — Plan 04 `anon_read_public_events` policy means
 * the only `events` row this client can ever see is one with
 * `visibility='public' AND status='scheduled' AND is_active=true`.
 *
 * The query predicates issued by `PublicCalendar` and
 * `PublicEventDetail` mirror those RLS literals as defense in depth.
 *
 * CRITICAL — `storageKey` MUST be unique (distinct from the authenticated
 * app-shell client's default `sb-<ref>-auth-token`). Without it, BOTH
 * GoTrueClient instances share the same storage key + navigator lock, which
 * Supabase warns "may produce undefined behavior when used concurrently".
 * In practice the anon client (instantiated on public routes like /calendar)
 * intermittently clobbers the authenticated client's session, so authenticated
 * requests then go out WITHOUT the user's JWT → the DB sees `anon` → RLS
 * returns zero rows → PostgREST 406 "Cannot coerce the result to a single JSON
 * object" on reads AND writes (e.g. saving a Person edit). A unique storageKey
 * isolates the two clients completely; this client never persists a session
 * anyway (persistSession:false), so the key is effectively a private namespace.
 */
export const supabaseAnon = createClient<Database>(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
    {
        auth: {
            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false,
            storageKey: 'sb-netscotland-public-anon',
        },
    }
);
